/**
 * Facebook and Google sign-in in the browser. Each hands back a provider access
 * token, which is what the legacy API's `/auth/facebook` and `/auth/google`
 * verify (against Graph `/me` and Google's userinfo endpoint respectively).
 *
 * The SDKs load only once the sign-in form is shown (`loadGoogle`,
 * `loadFacebook`): they are third-party scripts, and a visitor who never opens
 * the chat should not download them or be seen by Facebook and Google. They
 * must already be loaded when the button is clicked — browsers block a sign-in
 * popup that does not open within the click itself, so the sign-in functions
 * below open it synchronously and never await first.
 */

declare global {
  interface Window {
    google?: {
      accounts: {
        oauth2: {
          initTokenClient: (config: {
            client_id: string;
            scope: string;
            callback: (response: { access_token?: string; error?: string }) => void;
            error_callback?: (error: { type: string }) => void;
          }) => { requestAccessToken: () => void };
        };
      };
    };
    FB?: {
      init: (config: { appId: string; version: string; cookie?: boolean; xfbml?: boolean }) => void;
      login: (
        callback: (response: { authResponse?: { accessToken: string } | null }) => void,
        options: { scope: string },
      ) => void;
    };
  }
}

/** Raised when the visitor closes the provider's window without signing in. */
export class SignInCancelledError extends Error {
  constructor() {
    super('Sign-in cancelled');
    this.name = 'SignInCancelledError';
  }
}

const loading = new Map<string, Promise<void>>();

function loadScript(src: string): Promise<void> {
  const existing = loading.get(src);

  if (existing !== undefined) {
    return existing;
  }

  const promise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.async = true;
    script.src = src;
    script.onload = (): void => {
      resolve();
    };
    script.onerror = (): void => {
      loading.delete(src);
      reject(new Error(`Could not load ${src}`));
    };
    document.head.appendChild(script);
  });

  loading.set(src, promise);

  return promise;
}

export function loadGoogle(): Promise<void> {
  return loadScript('https://accounts.google.com/gsi/client');
}

export async function loadFacebook(): Promise<void> {
  await loadScript('https://connect.facebook.net/en_US/sdk.js');
}

/**
 * Google Identity Services' token client: the current replacement for the
 * `gapi.auth2` library the legacy frontend used, which Google has retired.
 * Call from a click handler, after `loadGoogle` has resolved.
 */
export function googleAccessToken(clientId: string): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const oauth2 = window.google?.accounts.oauth2;

    if (oauth2 === undefined) {
      reject(new Error('Google sign-in is not loaded'));

      return;
    }

    oauth2
      .initTokenClient({
        client_id: clientId,
        // What the legacy API reads from userinfo: id, e-mail and name.
        scope: 'openid email profile',
        callback: (response) => {
          if (response.access_token !== undefined) {
            resolve(response.access_token);
          } else {
            reject(new Error(response.error ?? 'Google sign-in failed'));
          }
        },
        error_callback: (error) => {
          reject(
            error.type === 'popup_closed' ? new SignInCancelledError() : new Error(error.type),
          );
        },
      })
      .requestAccessToken();
  });
}

let facebookAppId: string | undefined;

/** Call from a click handler, after `loadFacebook` has resolved. */
export function facebookAccessToken(appId: string): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const fb = window.FB;

    if (fb === undefined) {
      reject(new Error('Facebook sign-in is not loaded'));

      return;
    }

    if (facebookAppId !== appId) {
      fb.init({ appId, version: 'v19.0', cookie: false, xfbml: false });
      facebookAppId = appId;
    }

    fb.login(
      (response) => {
        const token = response.authResponse?.accessToken;

        if (token === undefined) {
          reject(new SignInCancelledError());
        } else {
          resolve(token);
        }
      },
      // E-mail is required: the `user` table keys accounts by it.
      { scope: 'public_profile,email' },
    );
  });
}
